/*
assets\component-cart-items.js
Tento soubor je upravený pro konfigurátor
*/

import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #bundleSyncInProgress = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * 🆕 BUNDLE: Získá bundle ID z řádku košíku
   * @param {HTMLTableRowElement} row - Řádek košíku
   * @returns {string | null} Bundle ID nebo null
   */
  #getBundleId(row) {
    return row.dataset.bundleId || null;
  }

  /**
   * 🆕 BUNDLE: Najde všechny řádky se stejným bundle ID
   * @param {string} bundleId - Bundle ID
   * @returns {HTMLTableRowElement[]} Pole řádků
   */
  #getBundleRows(bundleId) {
    return this.refs.cartItemRows.filter(row => this.#getBundleId(row) === bundleId);
  }

  /**
   * 🆕 BUNDLE: Získá line item key z řádku
   * @param {HTMLTableRowElement} row - Řádek košíku
   * @returns {string | null} Line item key
   */
  #getLineKey(row) {
    return row.dataset.key || null;
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  async #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    // 🆕 BUNDLE: Pokud probíhá synchronizace, ignoruj
    if (this.#bundleSyncInProgress) {
      console.log('⏸️ Synchronizace probíhá, přeskakuji...');
      return;
    }

    const lineItemRow = this.refs.cartItemRows[line - 1];
    const bundleId = lineItemRow ? this.#getBundleId(lineItemRow) : null;

    // 🆕 BUNDLE: Pokud je to bundle a množství je 0, odstraň celý bundle
    if (quantity === 0 && bundleId) {
      console.log('🗑️ Odstraňuji celý bundle přes mínus:', bundleId);
      return this.#removeBundleCompletely(bundleId);
    }

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    if (bundleId) {
      console.log('🔗 Detekován bundle:', bundleId, 'nové množství:', quantity);
      
      // Nastav flag že probíhá synchronizace
      this.#bundleSyncInProgress = true;

      try {
        // Synchronizuj všechny položky v bundle najednou
        await this.#syncBundleQuantities(bundleId, quantity);
      } finally {
        this.#bundleSyncInProgress = false;
      }
    } else {
      // Normální položka (ne bundle)
      this.updateQuantity({
        line,
        quantity,
        action: 'change',
      });

      if (!lineItemRow) return;

      const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
      textComponent?.shimmer();
    }
  }

  /**
   * 🆕 BUNDLE: Odstraň celý bundle najednou (batch update)
   * @param {string} bundleId - Bundle ID
   */
  async #removeBundleCompletely(bundleId) {
  const bundleRows = this.#getBundleRows(bundleId);
  
  console.log('🗑️ BUNDLE REMOVE - Start');
  console.log('   Bundle ID:', bundleId);
  console.log('   Počet položek:', bundleRows.length);

  this.#bundleSyncInProgress = true;
  this.#disableCartItems();

  // 🆕 Označ bundle rows pro odstranění PŘED update
  bundleRows.forEach(row => {
    row.setAttribute('data-removing', 'true');
  });

  const updates = {};
  bundleRows.forEach((row) => {
    const lineKey = this.#getLineKey(row);
    if (lineKey) {
      updates[lineKey] = 0;
    }
  });

  console.log('📦 Remove updates:', updates);

  try {
    const response = await fetch(`${Theme.routes.cart_update_url}`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ updates })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    console.log('✅ Remove OK');
    
    // 🆕 BEZPEČNÁ ANIMACE: Odstraň JEN označené řádky
    const rowsToRemove = Array.from(document.querySelectorAll('tr[data-removing="true"]'));
    
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) {
        remove();
      } else {
        row.style.setProperty('--row-height', `${row.clientHeight}px`);
        row.classList.add('removing');
        onAnimationEnd(row, remove);
      }
    });

    // Reload po animaci (300ms)
    setTimeout(() => location.reload(), 300);

  } catch (error) {
    console.error('❌ Chyba:', error);
    this.#showStockError('Nepodařilo se odebrat sadu.');
    setTimeout(() => location.reload(), 1500);
  } finally {
    this.#bundleSyncInProgress = false;
  }
}



/**
 * 🆕 BUNDLE: Synchronizuj množství všech položek v bundle pomocí hromadné aktualizace
 * @param {string} bundleId - Bundle ID
 * @param {number} newQuantity - Nové množství
 */
async #syncBundleQuantities(bundleId, newQuantity) {
  const bundleRows = this.#getBundleRows(bundleId);
  console.log(`🔄 BUNDLE SYNC: Používám hromadný update pro ${bundleRows.length} položek na množství ${newQuantity}.`);

  this.#disableCartItems();
  
  // Vytvoříme objekt s hromadnou změnou
  const updates = {};
  bundleRows.forEach(row => {
    const lineKey = this.#getLineKey(row);
    if (lineKey) {
      updates[lineKey] = newQuantity;
    }
  });

  // Pokud nemáme co aktualizovat, skončíme
  if (Object.keys(updates).length === 0) {
    this.#enableCartItems();
    return;
  }

  const body = JSON.stringify({ updates });

  try {
    const response = await fetch(`${Theme.routes.cart_update_url}`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body
    });

    if (!response.ok) {
      throw new Error(`HTTP chyba: ${response.status}`);
    }

    const cart = await response.json();
    
    // Po úspěšné aktualizaci je nejbezpečnější a nejjednodušší obnovit stránku,
    // aby se předešlo jakýmkoliv nesrovnalostem v zobrazení.
    location.reload();

  } catch (error) {
    console.error('❌ Chyba při hromadné aktualizaci sady:', error);
    this.#showStockError('Sadu se nepodařilo aktualizovat. Zkuste to prosím znovu.');
    // V případě chyby také raději obnovíme, aby se zobrazil správný stav
    setTimeout(() => location.reload(), 2000);
  } finally {
    // Ačkoliv se stránka obnovuje, pro jistotu košík odemkneme
    this.#enableCartItems();
  }
}


  /**
 * 🆕 Zobraz chybovou hlášku o nedostatku skladu
 * @param {string} message - Chybová zpráva
 */
#showStockError(message) {
  // Najdi nebo vytvoř error container
  let errorContainer = document.querySelector('.cart-bundle-error');
  
  if (!errorContainer) {
    errorContainer = document.createElement('div');
    errorContainer.className = 'cart-bundle-error';
    errorContainer.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #f44336;
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 9999;
      max-width: 400px;
      font-size: 14px;
      line-height: 1.5;
    `;
    document.body.appendChild(errorContainer);
  }

  errorContainer.textContent = message;
  errorContainer.style.display = 'block';
  
  // Automaticky skryj po 5 sekundách
  setTimeout(() => {
    if (errorContainer && errorContainer.parentNode) {
      errorContainer.style.display = 'none';
    }
  }, 5000);
}


  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    // 🆕 BUNDLE: Pokud je to bundle, použij batch odstranění
    const bundleId = this.#getBundleId(cartItemRowToRemove);
    
    if (bundleId) {
      console.log('🗑️ Odstraňuji bundle přes popelnici:', bundleId);
      return this.#removeBundleCompletely(bundleId);
    }

    // Normální odstranění (ne bundle)
    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    console.log('🗑️ Odstraňuji normální položku');

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
  const quantitySelector = this.refs.quantitySelectors[line - 1];
  const quantityInput = quantitySelector?.querySelector('input');

  const lineItemRow = this.refs.cartItemRows[line - 1];
  const bundleId = lineItemRow ? this.#getBundleId(lineItemRow) : null;

  if (bundleId) {
    console.log('🔄 BUNDLE ERROR HANDLING');
    console.log('   Chyba na line:', line);
    console.log('   Bundle ID:', bundleId);
    
    // 🆕 KROK 1: Získej SKUTEČNÉ množství z košíku (co Shopify nastavil)
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        console.log('📦 Košík načten:', cart);
        
        const bundleRows = this.#getBundleRows(bundleId);
        let mainBundleRow = null;
        let actualQuantity = 999; // Najdeme minimum
        
        // Najdi minimum množství v bundle
        bundleRows.forEach((row) => {
          if (row.classList.contains('cart-items__bundle-main')) {
            mainBundleRow = row;
          }
          
          const lineKey = this.#getLineKey(row);
          const cartItem = cart.items.find(item => item.key === lineKey);
          
          if (cartItem) {
            console.log('   Cart item:', cartItem.product_title, 'qty:', cartItem.quantity);
            actualQuantity = Math.min(actualQuantity, cartItem.quantity);
          }
        });

        if (actualQuantity === 999) {
          actualQuantity = 1; // Fallback
        }

        console.log('   ✅ Skutečné množství v košíku:', actualQuantity);

        // 🆕 KROK 2: RESETUJ všechny inputy na skutečné množství
        bundleRows.forEach((row) => {
          const rowInput = row.querySelector('input[type="number"]');
          if (rowInput) {
            console.log('   Resetuji input z', rowInput.value, 'na', actualQuantity);
            rowInput.value = actualQuantity;
            rowInput.defaultValue = actualQuantity; // Nastav i defaultValue
          }
        });

        if (!mainBundleRow) {
          console.error('❌ Main bundle row nenalezen!');
          return;
        }

        // 🆕 KROK 3: Zobraz hlášku s SPRÁVNÝM množstvím
        const mainBundleLine = this.refs.cartItemRows.indexOf(mainBundleRow) + 1;
        
        const cartItemError = this.refs[`cartItemError-${mainBundleLine}`];
        const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${mainBundleLine}`];

        if (cartItemError && cartItemErrorContainer) {
          let errorMessage;
          if (actualQuantity === 1) {
            errorMessage = 'Z důvodu dostupnosti byla do košíku přidána pouze 1 položka.';
          } else if (actualQuantity <= 4) {
            errorMessage = `Z důvodu dostupnosti byly do košíku přidány pouze ${actualQuantity} položky.`;
          } else {
            errorMessage = `Z důvodu dostupnosti bylo do košíku přidáno pouze ${actualQuantity} položek.`;
          }
          
          console.log('✅ Zobrazuji hlášku:', errorMessage);
          
          cartItemError.textContent = errorMessage;
          cartItemErrorContainer.classList.remove('hidden');
        } else {
          console.error('❌ Error element nenalezen!');
        }
      })
      .catch(error => {
        console.error('❌ Chyba při načítání košíku:', error);
      });
    
    return;
  }

  // Normální produkt
  if (!quantityInput) {
    return;
  }

  quantityInput.value = quantityInput.defaultValue;

  const cartItemError = this.refs[`cartItemError-${line}`];
  const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

  if (cartItemError && cartItemErrorContainer) {
    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  }
};



  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
